import { Settings } from '@/settings'
const { INDEX_DB_NAME, INDEX_DB_VERSION } = Settings

let DB
let promise

const getDB = async (name=undefined, version=undefined) => {
	if (DB) {
		return DB
	}
	
	if (promise) {
		return await promise
	}

	let resolve, reject
	promise = new Promise((res, rej) => {
		resolve = res
		reject = rej
	})

	let request

	try {
		request = indexedDB.open(name || INDEX_DB_NAME, version || INDEX_DB_VERSION)
	} catch (e) {
		console.error(e.message)
	}

	request.onerror = async (e) => {
		if (name && version) {
			console.error('Error opening indexedDB:', e.target.errorCode)
			reject(e)
			return
		}

		const res = await indexedDB.databases()
		const db = res.find((db) => db.name === INDEX_DB_NAME)

		if (db) {
			resolve(await getDB(INDEX_DB_NAME, db.version))
		} else {
			reject(new Error('Database not found'))
		}
	}

	request.onsuccess = (e) => {
		DB = e.target.result
		resolve(DB)
	}

	request.onupgradeneeded = (e) => {
		const db = e.target.result
        
		if (e.oldVersion < 1) {
			db.createObjectStore('http-cache', { keyPath: 'uid' })
			db.createObjectStore('store-cache', { keyPath: 'getter' })
		}
		
		if (e.oldVersion < 2) {
			db.createObjectStore('log-cache', { keyPath: 'uid' })
		}
	}

	return await promise
}

const getTransaction = async (store, oncomplete=undefined, onerror=undefined) => {
	let resolve, reject
	const promise = new Promise((res, rej) => {
		resolve = res
		reject = rej
	})

	const db = await getDB()
	const transaction = db.transaction([store], 'readwrite')

	transaction.oncomplete = oncomplete ? oncomplete : (e) => {
		resolve(e.target.result)
	}

	transaction.onerror = onerror ? onerror : (e) => {
		console.error('Error with transaction:', e.target.errorCode)
		reject(e)
	}

	return { transaction, promise, resolve, reject }
}

const insert = async (store, data) => {
	const { transaction, promise } = await getTransaction(store)
	transaction.objectStore(store).add(data)

	return await promise
}

const insertMany = async (store, data) => {
	const { transaction, promise } = await getTransaction(store)
	const request = transaction.objectStore(store)

	data.forEach((item) => {
		request.add(item)
	})

	return await promise
}

const upsert = async (store, data) => {
	const { transaction, promise } = await getTransaction(store)
	transaction.objectStore(store).put(data)

	return await promise
}

const upsertMany = async (store, data) => {
	const { transaction, promise } = await getTransaction(store)
	const request = transaction.objectStore(store)

	data.forEach((item) => {
		request.put(item)
	})

	return await promise
}

const get = async (store, key) => {
	const { transaction, promise, resolve } = await getTransaction(store)
	transaction.objectStore(store).get(key).onsuccess = (e) => {
		resolve(e.target.result)
	}

	return await promise
}

const list = async (store) => {
	const { transaction, promise, resolve } = await getTransaction(store, (e) => {})
	transaction.objectStore(store).getAll().onsuccess = (e) => {
		resolve(e.target.result)
	}

	return await promise
}

const keys = async (store) => {
	const { transaction, promise, resolve } = await getTransaction(store, (e) => {})
	transaction.objectStore(store).getAllKeys().onsuccess = (e) => {
		resolve(e.target.result)
	}

	return await promise
}

const remove = async (store, key) => {
	const { transaction, promise } = await getTransaction(store)
	transaction.objectStore(store).delete(key)

	return await promise
}

const each = async (store, cb) => {
	const { transaction, promise, resolve } = await getTransaction(store)

	transaction.objectStore(store).openCursor().onsuccess = (e) => {
		const cursor = e.target.result

		if (cursor) {
			cb(cursor)
			cursor.continue()
		} else {
			resolve()
		}
	}

	return await promise
}

const clear = async (store) => {
	const { transaction, promise, resolve } = await getTransaction(store)
	
	transaction.objectStore(store).clear().onsuccess = (e) => {
		resolve(e.target.result)
	}

	return await promise
}

const clearAll = async () => {
	const db = await getDB()
	const storeNames = db.objectStoreNames

	for (const store of storeNames) {
		await clear(store)
	}
}

export const idb = {
	getDB,
	getTransaction,
	insert,
	insertMany,
	upsert,
	upsertMany,
	get,
	list,
	keys,
	remove,
	each,
	clear,
	clearAll,
}